/**
 * @life/i18n — русские тексты по ключам.
 *
 * `t(key, params)` подставляет `{param}` из `params`; неизвестный ключ
 * возвращается как есть (так пропущенный текст виден в интерфейсе, а не
 * падает приложение). Плейсхолдеры без значения остаются нетронутыми.
 *
 * Производный параметр `{ageAt}`: если шаблон его просит, а в `params`
 * есть только число `age`, подставляется `ageAt(age)` — «В 1 год»,
 * «В 2 года», «В 5 лет», для нуля «В первый год жизни». Движок пишет
 * в журнал голое число, склонение — забота каталога.
 *
 * Строковый параметр, совпадающий с ключом каталога (например, название
 * команды `minigame.bets.team.3`), подставляется переведённым: логика
 * хранит ключи, перевод происходит при отрисовке.
 */
import { RU } from './ru';

export { RU };

export type I18nParams = Record<string, string | number>;

/** Русское склонение «год/года/лет» после числа. */
export function yearsWord(n: number): string {
  const abs = Math.abs(Math.trunc(n));
  const tens = abs % 100;
  const ones = abs % 10;
  if (tens >= 11 && tens <= 19) return 'лет';
  if (ones === 1) return 'год';
  if (ones >= 2 && ones <= 4) return 'года';
  return 'лет';
}

/** Обстоятельство возраста для записи журнала: «В 7 лет», для 0 — «В первый год жизни». */
export function ageAt(age: number): string {
  const n = Math.max(0, Math.trunc(age));
  if (n === 0) return 'В первый год жизни';
  return `В ${n} ${yearsWord(n)}`;
}

export function t(key: string, params?: I18nParams): string {
  const template = RU[key];
  if (template === undefined) return key;
  if (!params) return template;
  const withDerived: I18nParams =
    params['ageAt'] === undefined && typeof params['age'] === 'number' && template.includes('{ageAt}')
      ? { ...params, ageAt: ageAt(params['age']) }
      : params;
  return template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = withDerived[name];
    if (value === undefined) return match;
    if (typeof value === 'string' && RU[value] !== undefined) return RU[value];
    return String(value);
  });
}

/** Ключи каталога с данным префиксом (в порядке объявления). */
export function keysWithPrefix(prefix: string): string[] {
  return Object.keys(RU).filter((k) => k.startsWith(prefix));
}
